import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const DEFAULTS = {
  duration: 0.8,
  ease: "power2.out",
  start: "top 88%",
};

const ANIMATIONS = {
  "anim-fade-in": { autoAlpha: 0 },
  "anim-fade-up": { autoAlpha: 0, y: 32 },
  "anim-fade-down": { autoAlpha: 0, y: -32 },
  "anim-fade-left": { autoAlpha: 0, x: 32 },
  "anim-fade-right": { autoAlpha: 0, x: -32 },
  "anim-zoom-in": { autoAlpha: 0, scale: 0.92 },
};

const DURATION_CLASSES = {
  "anim-speed-fast": 0.4,
  "anim-speed-medium": 0.8,
  "anim-speed-slow": 1.2,
};

const DELAY_CLASSES = {
  "anim-delay-100": 0.1,
  "anim-delay-200": 0.2,
  "anim-delay-300": 0.3,
  "anim-delay-400": 0.4,
  "anim-delay-500": 0.5,
  "anim-delay-600": 0.6,
  "anim-delay-700": 0.7,
  "anim-delay-800": 0.8,
  "anim-delay-1000": 1,
};

function getDelay(el) {
  const val = el.dataset.animDelay;
  if (val) return parseFloat(val);
  for (const cls of Object.keys(DELAY_CLASSES)) {
    if (el.classList.contains(cls)) return DELAY_CLASSES[cls];
  }
  return 0;
}

function getDuration(el) {
  const val = el.dataset.animDuration;
  if (val) return parseFloat(val);
  for (const cls of Object.keys(DURATION_CLASSES)) {
    if (el.classList.contains(cls)) return DURATION_CLASSES[cls];
  }
  return DEFAULTS.duration;
}

function getAnimationType(el) {
  for (const cls of Object.keys(ANIMATIONS)) {
    if (el.classList.contains(cls)) return cls;
  }
  return null;
}

function toState(from) {
  return Object.fromEntries(
    Object.keys(from).map((key) => [
      key,
      key === "autoAlpha" || key === "scale" ? 1 : 0,
    ]),
  );
}

function animateElement(el) {
  const type = getAnimationType(el);
  if (!type) return;

  const from = { ...ANIMATIONS[type] };
  gsap.set(el, from);

  ScrollTrigger.create({
    trigger: el,
    start: el.dataset.animStart?.trim() || DEFAULTS.start,
    once: true,
    onEnter: () => {
      gsap.to(el, {
        ...toState(from),
        duration: getDuration(el),
        delay: getDelay(el),
        ease: DEFAULTS.ease,
        clearProps: "transform",
      });
    },
  });
}

function staggerChildren(container) {
  const children = Array.from(container.children);
  if (!children.length) return;

  const baseDelay = parseFloat(container.dataset.animStagger) || 0.1;
  const duration = getDuration(container);

  children.forEach((child) => {
    gsap.set(child, { autoAlpha: 0, y: 24 });
  });

  ScrollTrigger.create({
    trigger: container,
    start: container.dataset.animStart?.trim() || DEFAULTS.start,
    once: true,
    onEnter: () => {
      children.forEach((child, i) => {
        gsap.to(child, {
          autoAlpha: 1,
          y: 0,
          duration,
          delay: i * baseDelay,
          ease: DEFAULTS.ease,
          clearProps: "transform",
        });
      });
    },
  });
}

export function initEntranceAnimations() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const refresh = () => {
    requestAnimationFrame(() => ScrollTrigger.refresh());
  };
  window.addEventListener("load", refresh, { once: true });

  const animSelector = Object.keys(ANIMATIONS)
    .map((cls) => `.${cls}`)
    .join(",");

  document.querySelectorAll(animSelector).forEach(animateElement);
  document.querySelectorAll(".anim-stagger-children").forEach(staggerChildren);
}
